// Version: 1.2
// v1.2: Phone width: long permission names wrap inside their tag instead of running off the screen.
// v1.1: Buttons use the shared button system (CV-49): 8px corners, standard variants and sizes, one selected look; no inline radius, padding or font size.
// 1.0: Custom roles for staff users. An admin creates a role, ticks the
//      permissions it grants (sales area for now), and assigns it to a user on
//      the Users page. A staff user sees only what their role allows.
import React, { useEffect, useState } from 'react';
import { authFetch } from './auth';

export default function Roles() {
  const [roles, setRoles] = useState([]);
  const [perms, setPerms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editing, setEditing] = useState(null);   // {id?, name, permissions:Set}
  const [saving, setSaving] = useState(false);

  const flash = (msg, isErr = false) => {
    if (isErr) { setError(msg); setSuccess(''); } else { setSuccess(msg); setError(''); }
    setTimeout(() => { setError(''); setSuccess(''); }, 6000);
  };

  const load = () => authFetch('/api/admin/roles').then(r => r.json()).then(d => {
    setRoles(d.roles || []); setPerms(d.permissions || []); setLoading(false);
  }).catch(() => setLoading(false));

  useEffect(() => { load(); }, []);

  const groups = perms.reduce((acc, p) => { (acc[p.group] = acc[p.group] || []).push(p); return acc; }, {});

  const startNew = () => setEditing({ name: '', permissions: new Set() });
  const startEdit = (r) => setEditing({ id: r.id, name: r.name, permissions: new Set(r.permissions) });

  const toggle = (key) => setEditing(e => {
    const next = new Set(e.permissions);
    next.has(key) ? next.delete(key) : next.add(key);
    return { ...e, permissions: next };
  });

  const save = async () => {
    setSaving(true);
    const body = { name: editing.name.trim(), permissions: [...editing.permissions] };
    try {
      const url = editing.id ? `/api/admin/roles/${editing.id}` : '/api/admin/roles';
      const res = await authFetch(url, { method: editing.id ? 'PUT' : 'POST', body: JSON.stringify(body) });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || 'Save failed');
      flash(editing.id ? 'Role updated' : 'Role created');
      setEditing(null); load();
    } catch (e) { flash(e.message, true); } finally { setSaving(false); }
  };

  const remove = async (r) => {
    if (!window.confirm(`Delete the role "${r.name}"?`)) return;
    try {
      const res = await authFetch(`/api/admin/roles/${r.id}`, { method: 'DELETE' });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || 'Delete failed');
      flash('Role deleted'); load();
    } catch (e) { flash(e.message, true); }
  };

  const permLabel = (key) => (perms.find(p => p.key === key) || {}).label || key;

  return (
    <div>
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <div className="page-title">Roles</div>
          <div className="page-subtitle">Create roles and choose what each can do, then assign them to staff users</div>
        </div>
        {!editing && <button className="btn btn-primary" onClick={startNew}>+ New role</button>}
      </div>

      {error && <div className="error-msg" style={{ marginBottom: 16 }}>{error}</div>}
      {success && <div className="success-msg" style={{ marginBottom: 16 }}>{success}</div>}

      {editing && (
        <div className="card" style={{ marginBottom: 24 }}>
          <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 14 }}>{editing.id ? 'Edit role' : 'New role'}</div>
          <div style={{ marginBottom: 16 }}>
            <label>Role name</label>
            <input value={editing.name} onChange={e => setEditing({ ...editing, name: e.target.value })} placeholder="e.g. Sales Administrator" maxLength={60} />
          </div>
          {Object.entries(groups).map(([group, items]) => (
            <div key={group} style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: 8 }}>{group}</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {items.map(p => (
                  <label key={p.key} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', fontSize: 13, cursor: 'pointer' }}>
                    <input type="checkbox" checked={editing.permissions.has(p.key)} onChange={() => toggle(p.key)} style={{ width: 'auto', margin: '2px 0 0', flexShrink: 0 }} />
                    <span>{p.label}</span>
                  </label>
                ))}
              </div>
            </div>
          ))}
          <div style={{ display: 'flex', gap: 8, paddingTop: 4 }}>
            <button className="btn btn-primary" disabled={saving || !editing.name.trim()} onClick={save}>{saving ? <span className="spinner" /> : 'Save role'}</button>
            <button className="btn btn-secondary" onClick={() => setEditing(null)}>Cancel</button>
          </div>
        </div>
      )}

      {loading ? <div style={{ padding: 48, textAlign: 'center' }}><span className="spinner" /></div> : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {roles.length === 0 && !editing && (
            <div className="card" style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>
              No roles yet. Create one to give a staff user access to part of the platform without making them an admin.
            </div>
          )}
          {roles.map(r => (
            <div key={r.id} className="card" style={{ padding: '16px 20px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 700 }}>{r.name}</div>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>{r.users} user{r.users === 1 ? '' : 's'} · {r.permissions.length} permission{r.permissions.length === 1 ? '' : 's'}</div>
                </div>
                <div style={{ display: 'flex', gap: 6 }}>
                  <button className="btn btn-secondary btn-sm" onClick={() => startEdit(r)}>Edit</button>
                  <button className="btn btn-danger btn-sm" onClick={() => remove(r)}>Delete</button>
                </div>
              </div>
              {r.permissions.length > 0 && (
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 10 }}>
                  {r.permissions.map(k => <span key={k} className="tag tag-muted" style={{ fontSize: 10, whiteSpace: 'normal', maxWidth: '100%' }}>{permLabel(k)}</span>)}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
